import Plugin from '../core/Plugin';
import log from 'loglevel';

/**
 * 全屏插件插件
 *
 * @class Fullscreen
 * @extends {Plugin}
 */
export default class Fullscreen extends Plugin {
  constructor() {
    super();
  }

  init(opts = {}) {
    super.init(opts);
    this._allConfig = opts;
    let self = this;

    // 定义显示状态属性
    Object.defineProperty(this.player, 'displayState', {
      get() {
        return self._displayState || 'normal';
      },
      set(v) {
        self._displayState = v;
      },
      configurable: true
    });

    // 定义是否全屏属性
    Object.defineProperty(this.player, 'isFullscreen', {
      get() {
        return typeof self._isFullscreen === 'undefined' ? !!self.isFullscreen : self._isFullscreen;
      },
      configurable: true
    });

    // 注册进入全屏功能
    this.player.enterFullscreen = this.enterFullscreen.bind(this);
    // 注册离开全屏功能
    this.player.exitFullscreen = this.exitFullscreen.bind(this);

    // 监听全屏变化(当被劫持后无效)
    ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach(item => {
      const doc = window.__POWERED_BY_WUJIE__ ? window.top.document : window.document;
      doc.addEventListener(item, e => {
        // this.isFullscreen 在IE上返回一个对象，非IE返回Boolen值
        this._isFullscreen = !!this.isFullscreen;
        if (this._isFullscreen && this.player._clickExit) {
          this.player._clickExit = undefined;
        }
        this._fullscreenTarget = e.target;
        self._displayState = this._isFullscreen ? 'fullscreen' : 'normal';
        // log.info(`抛出全屏变化事件: ${!!this.isFullscreen}; isFullscreen=${this._isFullscreen}; displayState=${self._displayState}`);
        self.player.emit2All('fullscreenchanged', self._displayState);
      });
    });

    // 手机自动退出全屏时监听不到屏幕变化事件(微信小程序和没流时可以监听到)
    window.addEventListener('resize', e => {
      // log.info( `resize事件: ${this.isFullscreen}, ${this._isFullscreen}, ${self._displayState}, ${this.player._clickExit}`);
      // 安卓腾讯系使用MSE的情况下
      if (
        this._allConfig.x5 &&
        this.player &&
        this.player.player &&
        this.player.player._type !== 'NativePlayer' &&
        ((this._isFullscreen && this.isFullscreen && self._displayState === 'normal') || this.player._clickExit)
      ) {
        if (this.player._clickExit) {
          log.info('resize变了, x5调API退出全屏了');
          this.player._clickExit = undefined;
        } else {
          log.info('resize变了, x5自动退出全屏了');
        }
        this._isFullscreen = false;
        self._displayState = 'normal';
        // log.info( `抛出全屏变化事件: displayState=${self._displayState}`);
        this.player.emit2All('fullscreenchanged', self._displayState);
      }
    });
  }

  _handleSrcChange() {
    // log.info( `src变了: ${this.isFullscreen}, ${this._isFullscreen}, ${this._displayState}, ${this.player._clickExit}`);
    if (
      this._allConfig.x5 &&
      this.player &&
      this.player.player &&
      this.player.player._type !== 'NativePlayer' &&
      this._isFullscreen &&
      this.isFullscreen &&
      this._displayState === 'fullscreen'
    ) {
      log.info('src变了, 浏览器自动退出全屏了');
      this._isFullscreen = false;
      self._displayState = 'normal';
      this.player.emit2All('fullscreenchanged', self._displayState);
    }
  }

  /**
   * 进入全屏
   *
   * @memberof Fullscreen
   */
  enterFullscreen() {
    // H5下 全屏 el 需要为video？
    let el = this.player.getRoot(); // div容器元素
    if (el.webkitEnterFullScreen) {
      // Chrome等
      el.webkitEnterFullScreen();
    } else if (el.mozRequestFullScreen) {
      // FireFox
      el.mozRequestFullScreen();
    } else if (el.requestFullscreen) {
      // w3c
      el.requestFullscreen();
    } else if (el.msRequestFullscreen) {
      // IE11
      el.style.width = '100%';
      el.style.height = '100%';
      el.msRequestFullscreen();
    } else {
      // ios
      let sdk = this.player;
      let videoEl = sdk.player.video; // video元素
      if (videoEl.webkitEnterFullscreen || videoEl.enterFullScreen) {
        // ios微信在loadedmetadata 事件之前调用会抛出一个错
        videoEl.webkitEnterFullscreen && videoEl.webkitEnterFullscreen();
        videoEl.enterFullScreen && videoEl.enterFullScreen();
        videoEl.msRequestFullscreen && videoEl.msRequestFullscreen();
      }
    }

    this.player.displayState = 'fullscreen';
  }

  /**
   * 离开全屏
   *
   * @memberof Fullscreen
   */
  exitFullscreen() {
    const doc = window.__POWERED_BY_WUJIE__ ? window.top.document : window.document;
    if (doc.exitFullscreen) {
      // chrome
      doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen();
    } else if (doc.msExitFullscreen) {
      // IE11退出全屏时SaaS样式异常，将播放器容器宽高取消
      let el = this.player.getRoot();
      el.style.width = '';
      el.style.height = '';
      doc.msExitFullscreen();
    } else {
      // ios
      const video = this.player.player.video;
      if (video.webkitExitFullscreen) {
        video.webkitExitFullscreen();
      }
    }
    this.player.displayState = 'normal';
  }

  destroy() {
    super.destroy();
    delete this.player.displayState;
    delete this.player.isFullscreen;
  }

  /**
   * 是否是全屏
   *
   * @readonly
   * @memberof Fullscreen
   * @returns 根据 document 的 fullscreen 属性判断当前是否处于全屏状态
   */
  get isFullscreen() {
    const doc = window.__POWERED_BY_WUJIE__ ? window.top.document : window.document;
    return doc.fullScreen || doc.webkitIsFullScreen || doc.mozFullScreen || doc.msFullscreenElement || false;
  }

  /**
   * 如果当前正全屏， 返回全屏元素
   *
   * @readonly
   * @memberof Fullscreen
   * @returns 返回全屏元素
   */
  get fullscreenElement() {
    const doc = window.__POWERED_BY_WUJIE__ ? window.top.document : window.document;
    return (
      doc.fullscreenElement ||
      doc.webkitCurrentFullScreenElement ||
      doc.mozFullScreenElement ||
      this._fullscreenTarget ||
      null
    );
  }

  static get type() {
    return 'plugin_fullscreen';
  }
}
